import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getPaymentIntent, getGroupCredits } from '../utils/api';
import './PaymentReturn.css';

const REDIRECT_SECONDS = 5;
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 20000;

const PaymentReturn = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [amountPaid, setAmountPaid] = useState(null);
  const [balance, setBalance] = useState(null);
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);
  const [status, setStatus] = useState('loading');
  const countdownRef = useRef(null);
  const redirectStatus = searchParams.get('redirect_status');
  const paymentIntentSecret = searchParams.get('payment_intent_client_secret');

  useEffect(() => {
    if (redirectStatus !== 'succeeded' || !paymentIntentSecret) {
      setStatus('error');
      return;
    }

    const paymentIntentId = paymentIntentSecret.split('_secret_')[0];
    let cancelled = false;

    const poll = async () => {
      const start = Date.now();
      while (Date.now() - start < POLL_TIMEOUT_MS) {
        try {
          const data = await getPaymentIntent(paymentIntentId);
          if (cancelled) return;
          if (data?.webhook_processed) {
            setAmountPaid((data.amount / 100).toFixed(2));
            if (data.balance !== null) setBalance((data.balance / 100).toFixed(2));
            setStatus('success');
            return;
          }
        } catch {
          if (!cancelled) setStatus('error');
          return;
        }
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      }
      if (!cancelled) setStatus('error');
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [redirectStatus, paymentIntentSecret]);

  useEffect(() => {
    if (status !== 'success') return;
    countdownRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(countdownRef.current);
          navigate('/dashboard');
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [status, navigate]);

  const handleGoNow = () => {
    clearInterval(countdownRef.current);
    navigate('/dashboard');
  };

  if (status === 'loading') {
    return (
      <div className='pr-page'>
        <div className='pr-card'>
          <h1 className='pr-title'>Confirming payment...</h1>
          <p className='pr-subtitle'>Please wait while we verify your payment.</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className='pr-page'>
        <div className='pr-card pr-card--error'>
          <div className='pr-icon pr-icon--error'>✕</div>
          <h1 className='pr-title'>Payment Unsuccessful</h1>
          <p className='pr-subtitle'>
            Something went wrong. Please contact support if you were charged.
          </p>
          <button className='pr-btn' onClick={handleGoNow}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='pr-page'>
      <div className='pr-card'>
        <div className='pr-icon-wrap'>
          <svg className='pr-checkmark' viewBox='0 0 52 52'>
            <circle className='pr-checkmark__circle' cx='26' cy='26' r='25' fill='none' />
            <path className='pr-checkmark__check' fill='none' d='M14 27l8 8 16-16' />
          </svg>
        </div>
        <h1 className='pr-title'>Payment Successful</h1>
        <p className='pr-subtitle'>Your balance has been topped up.</p>
        <div className='pr-details'>
          {amountPaid && (
            <div className='pr-detail-row'>
              <span className='pr-detail-label'>Amount paid</span>
              <span className='pr-detail-value'>£{amountPaid}</span>
            </div>
          )}
          {balance !== null && (
            <div className='pr-detail-row pr-detail-row--highlight'>
              <span className='pr-detail-label'>New balance</span>
              <span className='pr-detail-value pr-detail-value--balance'>£{balance}</span>
            </div>
          )}
        </div>
        <button className='pr-btn' onClick={handleGoNow}>
          Go to Dashboard
        </button>
        <p className='pr-redirect-note'>
          Redirecting in <span className='pr-countdown'>{countdown}</span>s…
        </p>
      </div>
    </div>
  );
};

export default PaymentReturn;
